import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Curriculum } from './curriculum';
export { Curriculum };
import { curriculumSchema } from '@schemas/business-objects/curriculum';
import { ActivityFormArray } from './activity-form-group';
import { PrescriptionFormArray } from './prescription-form-group';
import { SessionFormArray } from './session-form-group';
import { UserFormGroup } from './user-form-group';
import type { ExportImportConfig } from '../interface-definitions/index';
import type { Resource } from './resource';
import type { TextBox } from './text-box';

export class CurriculumFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    shortName: SchemaFormControl;
    numberOfSessions: SchemaFormControl;
    sessionType: SchemaFormControl;
    locked: SchemaFormControl;
    uuid: SchemaFormControl;
    version: SchemaFormControl;
    status: SchemaFormControl;
    createdAt: SchemaFormControl;
    publishedAt: SchemaFormControl;
    archivedAt: SchemaFormControl;
    activities: ActivityFormArray;
    prescriptions: PrescriptionFormArray;
    sessions: SessionFormArray;
    createdById: SchemaFormControl;
    createdBy: UserFormGroup;
    publishedById: SchemaFormControl;
    publishedBy: UserFormGroup;
    archivedById: SchemaFormControl;
    archivedBy: UserFormGroup;
  };

  declare value: Partial<Curriculum>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(curriculumSchema, Curriculum, data, options);
  }

  get object(): Curriculum {
    return new Curriculum(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'activities':
        return new ActivityFormArray();
      case 'prescriptions':
        return new PrescriptionFormArray();
      case 'sessions':
        return new SessionFormArray();
      case 'createdBy':
        return new UserFormGroup();
      case 'publishedBy':
        return new UserFormGroup();
      case 'archivedBy':
        return new UserFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Curriculum[]> {
    return Curriculum.find(filter);
  }

  static async findOne(filter?: any): Promise<Curriculum | null> {
    return Curriculum.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<Curriculum> {
    return Curriculum.findById(id, filter);
  }

  static async create(data: Partial<Curriculum>): Promise<Curriculum> {
    return Curriculum.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<Curriculum>,
  ): Promise<Curriculum> {
    return Curriculum.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return Curriculum.deleteById(id);
  }

  async export(): Promise<any> {
    const instance = new Curriculum(this.getRawValue());
    return instance.export();
  }

  static async exportMany(body: any): Promise<any> {
    return Curriculum.exportMany(body);
  }

  async publish(): Promise<any> {
    const instance = new Curriculum(this.getRawValue());
    return instance.publish();
  }

  async newVersion(): Promise<any> {
    const instance = new Curriculum(this.getRawValue());
    return instance.newVersion();
  }

  async latestVersion(): Promise<any> {
    const instance = new Curriculum(this.getRawValue());
    return instance.latestVersion();
  }

  static async findLatestVersions(
    filter: any,
    publishedOnly?: boolean,
  ): Promise<any[]> {
    return Curriculum.findLatestVersions(filter, publishedOnly);
  }

  async archive(): Promise<any> {
    const instance = new Curriculum(this.getRawValue());
    return instance.archive();
  }

  async dataForCopy(): Promise<any> {
    const instance = new Curriculum(this.getRawValue());
    return instance.dataForCopy();
  }
}

export class CurriculumFormArray extends PersistedFormArray {
  readonly entityName = 'Curriculum' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(curriculumSchema, Curriculum, options);
  }

  protected override createItemGroup() {
    return new CurriculumFormGroup();
  }

  get array(): Curriculum[] {
    return this.controls.map((group: CurriculumFormGroup) => group.object);
  }
}

export class CurriculumPersistedArray extends PersistedArray<Curriculum> {
  readonly entityName = 'Curriculum' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Curriculum, options);
  }
}
